import React, { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import { Map } from "immutable";
import { Container } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import { ManifestListParams } from "types/api/history";
import { OpenRightPanel } from "types/common/rightPanel";
import LoadingBar from "../../components/LoadingBar/LoadingBar";
import PreLoader from "../../components/PreLoader/PreLoader";
import injectReducer from "../../reduxFolder/injectReducer";
import historyReducer, {
  getManifests,
} from "../../reduxFolder/reducers/historyReducer";
import { openRightPanel } from "../../reduxFolder/reducers/rightPanel";
import { authSelectUnits } from "../../reduxFolder/selectors/globalSelectors";
import {
  historySelectAvailableYears,
  historySelectCurrentPage,
  historySelectHasMorePages,
  historySelectIsLoadingMore,
  historySelectItems,
  historySelectLoading,
  historySelectWeekRanges,
} from "../../reduxFolder/selectors/historySelector";
import { formatConsolidationSku } from "../../utils/consolidationSku";
import { toastResponseError } from "../../utils/responseMessageHelper";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import HistoryHeader from "./components/HistoryHeader/HistoryHeader";
import YearSelector from "./components/YearSelector/YearSelector";
import Week from "./components/Week/Week";
import { HistoryEmpty, Wrapper } from "./History.styles";

interface HistoryProps {
  items: any;
  weekRanges: any;
  openRightPanel: OpenRightPanel;
  units: string;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMorePages: boolean;
  currentPage: number;
  availableYears: any;
  getManifests: (params: ManifestListParams) => Promise<any>;
}

const History: FC<HistoryProps> = ({
  items,
  weekRanges,
  isLoading,
  isLoadingMore,
  hasMorePages,
  currentPage,
  availableYears,
  getManifests,
  units,
  openRightPanel,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const searchParamsRef = useRef<ManifestListParams>({});

  const weekKeys = items.size
    ? (items.keySeq().toArray() as string[]).sort().reverse()
    : [];
  const hasItems = weekKeys.length > 0;

  const years: number[] = availableYears && availableYears.size
    ? availableYears.toJS()
    : [];

  // Set default year from API response (most recent year)
  useEffect(() => {
    if (selectedYear === null && years.length > 0) {
      setSelectedYear(years[0]);
    }
  }, [selectedYear, years]);

  // Initial load and year change
  useEffect(() => {
    const state = location.state as any;

    if (state && state.consolidationId) {
      // Navigated from Storage: find the manifest containing this consolidation
      const consolidationSku = formatConsolidationSku(state.consolidationId);

      getManifests({
        "q[orders_consolidation_sku_eq]": consolidationSku,
      })
        .then((response: any) => {
          const manifests = response?.value?.getIn?.(["data", "manifests"]);
          const firstManifest = manifests && manifests.size > 0 ? manifests.get(0) : null;
          if (firstManifest) {
            openRightPanel(
              "MANIFEST_DETAIL",
              Map({
                manifestId: firstManifest.get("id"),
                batchNumber: firstManifest.get("batch_number"),
                shipperName: firstManifest.getIn(["manifest_shipper", "name"], ""),
                foundConsolidationId: state.consolidationId,
              }),
            );
          }
        })
        .catch(toastResponseError);

      navigate(location.pathname, { state: undefined, replace: true });
    } else if (selectedYear !== null) {
      searchParamsRef.current = {};
      getManifests({ page: 1, "q[created_at_year_eq]": selectedYear }).catch(toastResponseError);
    } else {
      // First load: fetch without year filter to get available years
      getManifests({ page: 1 }).catch(toastResponseError);
    }
  }, [selectedYear]);

  const handleSearch = useCallback(
    ({
      trackingNumber,
      consolidationSku,
    }: {
      trackingNumber?: string;
      consolidationSku?: string;
    }) => {
      const params: ManifestListParams = {};
      if (trackingNumber) {
        params["q[orders_tracking_number_cont]"] = trackingNumber;
      }
      if (consolidationSku) {
        params["q[orders_consolidation_sku_cont]"] = consolidationSku;
      }
      searchParamsRef.current = params;
      const yearParam: ManifestListParams = selectedYear !== null
        ? { "q[created_at_year_eq]": selectedYear }
        : {};
      getManifests({ page: 1, ...yearParam, ...params }).catch(
        toastResponseError,
      );
    },
    [getManifests, selectedYear],
  );

  const handleYearChange = useCallback((year: number) => {
    setSelectedYear(year);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (isLoadingMore || isLoading) return;
    const yearParam: ManifestListParams = selectedYear !== null
      ? { "q[created_at_year_eq]": selectedYear }
      : {};
    getManifests({
      page: currentPage + 1,
      ...yearParam,
      append: true,
      ...searchParamsRef.current,
    }).catch(toastResponseError);
  }, [getManifests, currentPage, selectedYear, isLoadingMore, isLoading]);

  const sentinelRef = useInfiniteScroll(handleLoadMore, {
    enabled: hasMorePages && !isLoading && !isLoadingMore,
  });

  return (
    <Wrapper>
      <Container fluid>
        <LoadingBar isLoading={isLoading} isCartLoading={false} />
        {selectedYear !== null && (
          <YearSelector
            availableYears={years}
            selectedYear={selectedYear}
            onYearChange={handleYearChange}
          />
        )}
        <HistoryHeader onSearch={handleSearch} />
        {!isLoading && hasItems &&
          weekKeys.map((weekKey) => (
            <Week
              units={units}
              key={weekKey}
              weekKey={weekKey}
              weekRange={weekRanges.get(weekKey, "")}
              manifests={items.get(weekKey)}
            />
          ))}
        {!isLoading && !hasItems && (
          <HistoryEmpty>{t("parcels.emptyHistory")}</HistoryEmpty>
        )}
        {isLoadingMore && <PreLoader />}
        <div ref={sentinelRef} />
      </Container>
    </Wrapper>
  );
};

const withReducer = injectReducer({
  key: "history",
  reducer: historyReducer,
});

const withConnect = connect(
  createStructuredSelector({
    units: authSelectUnits,
    items: historySelectItems,
    weekRanges: historySelectWeekRanges,
    isLoading: historySelectLoading,
    isLoadingMore: historySelectIsLoadingMore,
    hasMorePages: historySelectHasMorePages,
    currentPage: historySelectCurrentPage,
    availableYears: historySelectAvailableYears,
  }),
  {
    getManifests,
    openRightPanel,
  },
);

const enhance = compose(withReducer, withConnect, memo);

export default enhance(History);
